import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Calendar, Gauge, Fuel,
  Settings, Car, Palette, ChevronLeft, ChevronRight, Check, Image,
  AlertTriangle, ClipboardCheck, FileText, Download, Eye, Wrench, Play,
  MapPin, History, Key, Users, ShieldCheck, Globe, BookOpen, Landmark,
  Info, RefreshCw, Send, MessageCircle, Share2, ChevronDown,
  Phone, PhoneCall, Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { WhatsAppGroupButton, useWhatsAppGroupUrl } from "@/components/WhatsAppGroupButton";
import { SmartImage, preloadImage } from "@/components/SmartImage";
import { Lightbox } from "@/components/Lightbox";
import { PaintDiagram } from "@/components/PaintDiagram";
import { TireInspection } from "@/components/TireInspection";
import { AvailabilityBadge } from "@/components/AvailabilityBadge";
import { InspectionStamp } from "@/components/InspectionStamp";
import { cldThumb, cldVideoPoster } from "@/lib/cld";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "436641104878";

const INSPECTION_FIELDS = [
  { key: "motor", label: "Motor állapota" },
  { key: "valto", label: "Váltó működése" },
  { key: "futumu", label: "Futómű állapota" },
  { key: "fekek", label: "Fékek állapota" },
  { key: "belso", label: "Belső tér állapota" },
  { key: "menet", label: "Menet közbeni tapasztalatok" },
  { key: "egyeb", label: "Egyéb megjegyzések" },
];

const COUNTRY_FLAGS = { "Ausztria": "🇦🇹", "Magyarország": "🇭🇺", "Németország": "🇩🇪" };

// AlpineCar inspection value items (section 9)
const CHECK_ITEMS = [
  { icon: ClipboardCheck, title: "Állapotellenőrzés", desc: "A jármű fontosabb műszaki és külső-belső állapotának ellenőrzése." },
  { icon: FileText, title: "Dokumentáció", desc: "A rendelkezésre álló járműdokumentumok és adatok áttekintése." },
  { icon: Info, title: "Átlátható információk", desc: "A vásárlás előtt megismerhető fontos információkat egy helyen mutatjuk be." },
  { icon: ShieldCheck, title: "AlpineCar ellenőrzés", desc: "A járművet az AlpineCar saját ellenőrzési folyamatán keresztül vizsgálja meg." },
];

// Documentation / Hungarian registration items (section 10)
const ADMIN_ITEMS = [
  { icon: Wrench, title: "Műszaki", desc: "A szükséges műszaki ügyintézés megszervezése." },
  { icon: Globe, title: "Honosítás", desc: "Az osztrák jármű magyarországi honosításának ügyintézése." },
  { icon: Landmark, title: "Regisztrációs adó", desc: "A regisztrációs adóhoz kapcsolódó ügyintézés." },
  { icon: FileText, title: "Dokumentáció", desc: "A magyarországi forgalomba helyezéshez szükséges dokumentáció kezelése." },
];

// Collapsible section with animated expand/collapse
const AccordionSection = ({ icon: Icon, title, teaser, defaultOpen = false, accent = false, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-xl sm:rounded-2xl overflow-hidden border ${accent ? "border-[#D4AF37]/25 bg-gradient-to-br from-[#0A0A0A] to-[#0d0b06]" : "border-[#D4AF37]/15 bg-[#0A0A0A]"}`} data-testid={testId}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-5 sm:p-6 text-left group"
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
      >
        <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37]/20 transition-colors">
          <Icon className="w-5 h-5 text-[#D4AF37]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-lg sm:text-xl leading-tight">{title}</h2>
          {teaser && <p className="text-xs text-white/45 mt-0.5 line-clamp-1">{teaser}</p>}
        </div>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37]/15 transition-colors"
        >
          <ChevronDown className="w-4 h-4 text-[#D4AF37]" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 sm:px-6 pb-5 sm:pb-6">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function CarDetail() {
  const { id } = useParams();
  const waGroupUrl = useWhatsAppGroupUrl();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [contact, setContact] = useState({ name: "", phone: "", message: "" });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactInfo, setContactInfo] = useState({ phone: "+36 20 412 2950", whatsapp: "436641104878", email: "info@alpinecar.eu" });
  const [quickPanelOpen, setQuickPanelOpen] = useState(false);

  const fetchCar = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cars/${id}`);
      setCar(response.data);
    } catch (error) {
      toast.error("Hiba az autó betöltésekor");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchCar(); }, [fetchCar]);

  useEffect(() => {
    axios.get(`${API}/settings/contact`)
      .then((res) => { if (res.data) setContactInfo(res.data); })
      .catch(() => {});
  }, []);

  // Preload first car image for instant LCP
  useEffect(() => {
    if (car?.images?.length > 0) {
      preloadImage(car.images[0], "med");
    }
  }, [car]);

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;
  };

  const getMediumUrl = (url) => {
    if (!url) return null;
    let medUrl = url.replace("_full.webp", "_med.webp");
    if (medUrl === url && (url.endsWith(".jpeg") || url.endsWith(".jpg"))) {
      medUrl = url.replace(/\.(jpeg|jpg)$/, "_med.webp");
    }
    return medUrl.startsWith("/api") ? `${BACKEND_URL}${medUrl}` : medUrl;
  };

  const getThumbUrl = (url) => {
    if (!url) return null;
    let thumbUrl = url.replace("_full.webp", "_thumb.webp");
    if (thumbUrl === url && (url.endsWith(".jpeg") || url.endsWith(".jpg"))) {
      thumbUrl = url.replace(/\.(jpeg|jpg)$/, "_thumb.webp");
    }
    return thumbUrl.startsWith("/api") ? `${BACKEND_URL}${thumbUrl}` : thumbUrl;
  };

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const scrollToContact = () => {
    const el = document.getElementById("kapcsolat");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const shareCar = async () => {
    if (!car) return;
    const url = window.location.href;
    const title = `${car.brand} ${car.model}`;
    const text = `${car.brand} ${car.model} (${car.year}) - ${car.price} — AlpineCar`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link vágólapra másolva");
      }
    } catch (err) { /* user cancelled share */ }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!car) return;
    setContactSubmitting(true);
    try {
      const carLabel = `${car.brand} ${car.model} (${car.year}) - ${car.price}`;
      await axios.post(`${API}/inquiries`, {
        name: contact.name,
        phone: contact.phone,
        budget: car.price || "-",
        brand: car.brand || "-",
        model: car.model || "",
        year: (car.year || "").toString(),
        mileage: (car.mileage || "").toString(),
        message: `Érdeklődés a következő autó iránt: ${carLabel}.\n${contact.message || ""}`.trim(),
      });
      setContactSubmitted(true);
      toast.success("Köszönjük! Hamarosan felvesszük Önnel a kapcsolatot.");
      setContact({ name: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Hiba történt a küldés során");
    } finally {
      setContactSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen bg-[#030303] flex flex-col items-center justify-center text-white px-4">
        <h1 className="text-xl font-bold mb-4">Autó nem található</h1>
        <Link to="/" className="text-[#D4AF37] hover:underline">Vissza a főoldalra</Link>
      </div>
    );
  }

  const allImages = car.images?.length > 0 ? car.images : [];
  const hasImages = allImages.length > 0;
  const inspection = car.inspection_report || {};
  const hasInspection = Object.values(inspection).some(v => v && v !== "" && v !== false);
  const vehicleInspectionData = car.vehicle_inspection || {};
  const hasVehicleInspection = Object.values(vehicleInspectionData).some(v => v !== "" && v !== undefined && v !== null);
  const isInspected = hasInspection || hasVehicleInspection;
  const damages = car.damages || [];
  const documents = car.documents || [];
  const motorImages = car.motor_images || [];
  const location = (car.vehicle_origin || {}).location || "";
  const isSold = car.sold || car.status === "sold";

  const specs = [
    { label: "Évjárat", value: car.year, icon: <Calendar className="w-4 h-4" /> },
    { label: "Kilométer", value: car.mileage, icon: <Gauge className="w-4 h-4" /> },
    { label: "Üzemanyag", value: car.fuel, icon: <Fuel className="w-4 h-4" /> },
    { label: "Váltó", value: car.transmission, icon: <Settings className="w-4 h-4" /> },
    { label: "Motor", value: car.engine || "-", icon: <Car className="w-4 h-4" /> },
    { label: "Teljesítmény", value: car.power || "-", icon: <Settings className="w-4 h-4" /> },
    { label: "Meghajtás", value: car.drive || "-", icon: <Car className="w-4 h-4" /> },
    { label: "Karosszéria", value: car.body_type || "-", icon: <Car className="w-4 h-4" /> },
    { label: "Szín", value: car.color || "-", icon: <Palette className="w-4 h-4" /> },
  ];

  const waNumber = (contactInfo.whatsapp || WHATSAPP_NUMBER).replace(/[^0-9]/g, "");
  const carLabelShort = `${car.brand} ${car.model} (${car.year}) - ${car.price}`;
  const whatsappMessage = encodeURIComponent(`Szia! Érdeklődöm: ${carLabelShort}`);
  const whatsappHref = `https://wa.me/${waNumber}?text=${whatsappMessage}`;
  const whatsappCallHref = `https://wa.me/${waNumber}`;
  const telHref = `tel:${(contactInfo.phone || "").replace(/\s/g, "")}`;
  const emailHref = `mailto:${contactInfo.email}?subject=${encodeURIComponent("Érdeklődés - " + carLabelShort)}&body=${encodeURIComponent("Szia!\n\nÉrdeklődöm a következő autó iránt: " + carLabelShort + "\n\nKérem, keressenek meg.\n")}`;

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#030303]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white text-sm">
              <ArrowLeft className="w-4 h-4" /> Vissza
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <img src="/alpine-logo-full.png" alt="Alpine Car" className="h-9 sm:h-11 w-auto object-contain" />
            </Link>
            <button
              onClick={shareCar}
              className="flex items-center gap-1.5 text-white/60 hover:text-[#D4AF37] text-sm transition-colors"
              data-testid="share-car-header-btn"
              aria-label="Autó megosztása"
            >
              <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">Megosztás</span>
            </button>
          </div>
        </div>
      </header>

      <main className="pt-16 sm:pt-20 pb-16">
        {/* 1. Gallery */}
        <div className="mb-4 sm:mb-8 sm:max-w-7xl sm:mx-auto sm:px-6">
          <div
            className="aspect-[4/3] sm:aspect-[21/9] sm:rounded-2xl overflow-hidden bg-[#0A0A0A] cursor-pointer relative"
            onClick={() => hasImages && openLightbox(allImages, activeImage)}
            data-testid="car-main-image"
          >
            {hasImages ? (
              <>
                <SmartImage
                  src={allImages[activeImage]}
                  variant="med"
                  srcset
                  priority
                  sizes="(max-width: 768px) 100vw, 100vw"
                  alt={`${car.brand} ${car.model}`}
                  className="w-full h-full"
                  testId="car-main-image-el"
                />
                <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/60 backdrop-blur-sm rounded-lg flex items-center gap-1.5 text-white/70 text-xs pointer-events-none">
                  <Eye className="w-3.5 h-3.5" /> Nagyítás
                </div>
                {isSold && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center pointer-events-none">
                    <div className="bg-red-600 text-white font-bold text-xl sm:text-2xl px-8 py-3 rounded-lg -rotate-6 shadow-xl border-2 border-red-400" data-testid="car-sold-overlay">
                      ELADVA
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/20">
                <Image className="w-16 h-16" />
              </div>
            )}
          </div>

          {allImages.length > 1 && (
            <div className="relative px-2 sm:px-0">
              <button onClick={(e) => { e.stopPropagation(); setActiveImage(prev => prev === 0 ? allImages.length - 1 : prev - 1); }} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center z-10" style={{ top: "-50%" }}>
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setActiveImage(prev => prev === allImages.length - 1 ? 0 : prev + 1); }} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center z-10" style={{ top: "-50%" }}>
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="flex gap-2 justify-center mt-3 overflow-x-auto pb-2">
                {allImages.map((img, i) => (
                  <button key={img || i} onClick={() => openLightbox(allImages, i)} className={`w-16 h-12 sm:w-20 sm:h-14 rounded-lg overflow-hidden border-2 flex-shrink-0 ${activeImage === i ? 'border-[#D4AF37]' : 'border-transparent opacity-50 hover:opacity-80'}`}>
                    <SmartImage src={img} variant="thumb" alt="" className="w-full h-full" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Full-width content */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-6 relative">
          {/* Desktop inspection stamp — absolute top-right */}
          {isInspected && !isSold && (
            <InspectionStamp className="hidden lg:block lg:absolute lg:top-0 lg:right-2 lg:z-20 w-[280px]" />
          )}
          {/* Title (+ compact stamp next to name on mobile) */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1">{car.brand} {car.model} {car.engine && <span className="text-white/70 font-normal">{car.engine}</span>}</h1>
              <p className="text-white/50 text-sm flex flex-wrap items-center gap-x-1.5">
                <span>{car.year}</span><span className="text-white/20">-</span>
                <span>{car.mileage}</span>
                {car.origin_country && <><span className="text-white/20">-</span><span>{COUNTRY_FLAGS[car.origin_country] || ""} {car.origin_country}</span></>}
              </p>
            </div>
            {isInspected && !isSold && (
              <InspectionStamp compact className="lg:hidden flex-shrink-0 mt-0.5" />
            )}
          </div>

          {/* Availability badge (inspected status now shown via animated stamp) */}
          <div className="flex flex-wrap items-center gap-3">
            <AvailabilityBadge car={car} size="lg" />
          </div>

          {/* Price + Location + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-2xl sm:rounded-3xl p-[1.5px] bg-gradient-to-br from-[#D4AF37]/70 via-[#D4AF37]/15 to-transparent"
            data-testid="price-cta-card"
          >
            <div className="relative rounded-2xl sm:rounded-3xl bg-[#08080a] overflow-hidden p-5 sm:p-7">
              {/* Ambient glow */}
              <motion.div
                aria-hidden="true"
                className="absolute -top-20 -right-12 w-56 h-56 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(212,175,55,0.20), transparent 70%)" }}
                animate={{ opacity: [0.45, 0.85, 0.45], scale: [1, 1.1, 1] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Shimmer sweep */}
              <motion.div
                aria-hidden="true"
                className="absolute inset-y-0 -left-1/4 w-1/4 skew-x-12 pointer-events-none"
                style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)" }}
                animate={{ x: ["0%", "620%"] }}
                transition={{ duration: 3.2, repeat: Infinity, repeatDelay: 2.6, ease: "easeInOut" }}
              />

              <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
                <div>
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/40 mb-1.5">
                    <span className="w-6 h-px bg-[#D4AF37]/50" /> Autó ára
                  </div>
                  <div
                    className="text-4xl sm:text-5xl font-extrabold leading-none tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#F6E27A] via-[#D4AF37] to-[#b8901f]"
                    style={{ filter: "drop-shadow(0 2px 14px rgba(212,175,55,0.25))" }}
                    data-testid="car-price"
                  >
                    {car.price}
                  </div>
                  {location && (
                    <div className="flex items-center gap-1.5 mt-4 text-sm" data-testid="car-location">
                      <MapPin className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-white/40">Jármű helye:</span>
                      <span className="text-white font-medium">{location}</span>
                    </div>
                  )}
                </div>
                {isSold ? (
                  <div className="px-5 py-3 bg-white/5 border border-white/10 rounded-xl text-center" data-testid="car-sold-badge">
                    <span className="text-sm font-bold text-white/60 uppercase tracking-wide">Eladva</span>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3 sm:min-w-[360px]">
                    <motion.button
                      onClick={() => setQuickPanelOpen((o) => !o)}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className="group relative flex-1 py-4 px-6 bg-gradient-to-r from-[#F0D77B] to-[#D4AF37] text-black text-center font-bold rounded-xl text-sm shadow-[0_6px_24px_rgba(212,175,55,0.35)] inline-flex items-center justify-center gap-2 overflow-hidden"
                      data-testid="interested-cta"
                      aria-expanded={quickPanelOpen}
                    >
                      <span className="relative z-10">Érdekel az autó</span>
                      <motion.span animate={{ rotate: quickPanelOpen ? 180 : 0 }} transition={{ duration: 0.3 }} className="relative z-10 flex">
                        <ChevronDown className="w-4 h-4" />
                      </motion.span>
                      <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)" }} />
                    </motion.button>
                    <motion.button
                      onClick={scrollToContact}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className="flex-1 py-4 px-6 bg-white/[0.04] border border-white/10 text-white text-center font-semibold rounded-xl text-sm hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/[0.06] transition-colors inline-flex items-center justify-center gap-2"
                      data-testid="contact-cta"
                    >
                      <Send className="w-4 h-4 text-[#D4AF37]" /> Üzenet írása
                    </motion.button>
                  </div>
                )}
              </div>

              {/* Quick contact panel (opened by "Érdekel az autó") */}
              {!isSold && (
                <AnimatePresence initial={false}>
                  {quickPanelOpen && (
                    <motion.div
                      key="quick"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                      className="relative overflow-hidden"
                      data-testid="quick-contact-panel"
                    >
                      <div className="mt-5 pt-5 border-t border-white/10">
                        <p className="text-sm text-white/60 mb-3">Lépjen kapcsolatba velünk ezzel az autóval kapcsolatban — a gombok automatikusan elindítják a folyamatot:</p>
                        <div className="grid sm:grid-cols-3 gap-2.5">
                          <a
                            href={whatsappHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 py-3.5 px-4 bg-[#25D366]/15 border border-[#25D366]/35 text-[#25D366] font-semibold rounded-xl hover:bg-[#25D366]/25 text-sm transition-colors"
                            data-testid="quick-whatsapp-msg"
                          >
                            <MessageCircle className="w-4 h-4" /> WhatsApp üzenet
                          </a>
                          <a
                            href={whatsappCallHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 py-3.5 px-4 bg-white/[0.04] border border-[#25D366]/35 text-[#25D366] font-semibold rounded-xl hover:bg-[#25D366]/15 text-sm transition-colors"
                            data-testid="quick-whatsapp-call"
                          >
                            <PhoneCall className="w-4 h-4" /> WhatsApp hívás
                          </a>
                          <a
                            href={emailHref}
                            className="flex items-center justify-center gap-2 py-3.5 px-4 bg-[#D4AF37]/15 border border-[#D4AF37]/35 text-[#D4AF37] font-semibold rounded-xl hover:bg-[#D4AF37]/25 text-sm transition-colors"
                            data-testid="quick-email"
                          >
                            <Mail className="w-4 h-4" /> Email küldése
                          </a>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 p-3.5 bg-white/[0.03] border border-white/5 rounded-xl" data-testid="quick-phone">
                          <Phone className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                          <span className="text-sm text-white/55">Ha érdekli, telefonon is elérhetők vagyunk:</span>
                          <a href={telHref} className="text-sm font-bold text-[#D4AF37] hover:underline" data-testid="quick-phone-number">{contactInfo.phone}</a>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          </motion.div>

          {/* Műszaki adatok */}
          <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6">
            <h2 className="font-semibold mb-4 sm:mb-6">Műszaki adatok</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {specs.filter(s => s.value && s.value !== "-").map((spec, i) => (
                <div key={i} className="text-center p-3 bg-white/5 rounded-xl">
                  <div className="w-8 h-8 mx-auto mb-2 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center text-[#D4AF37]">{spec.icon}</div>
                  <div className="text-xs text-white/40 mb-0.5">{spec.label}</div>
                  <div className="font-medium text-sm">{spec.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          {car.description && (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6">
              <h2 className="font-semibold mb-3">Leírás</h2>
              <p className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap">{car.description}</p>
            </div>
          )}

          {/* AlpineCar inspection (collapsible) */}
          <AccordionSection
            icon={ShieldCheck}
            title="AlpineCar ellenőrzés"
            teaser="Az autó állapotát a vásárlás előtt ellenőrizzük."
            testId="alpinecar-inspection-section"
          >
            <p className="text-sm text-white/55 leading-relaxed mb-5">
              Az autó állapotát a vásárlás előtt ellenőrizzük, és a rendelkezésre álló fontos információkat áttekintjük.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
              {CHECK_ITEMS.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-start gap-3 p-4 bg-white/[0.03] border border-white/5 rounded-xl">
                    <div className="w-9 h-9 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0 text-[#D4AF37]">
                      <Icon className="w-[18px] h-[18px]" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-white mb-0.5">{item.title}</div>
                      <div className="text-xs text-white/50 leading-relaxed">{item.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </AccordionSection>

          {/* Documentation / Hungarian registration (collapsible) */}
          <AccordionSection
            icon={Landmark}
            title="Mi intézzük a szükséges ügyintézést."
            teaser="Műszaki, honosítási és regisztrációs ügyintézés."
            accent
            testId="documentation-section"
          >
            <p className="text-sm text-white/55 leading-relaxed mb-5">
              Ausztriából Magyarországra érkező autó esetén elintézzük a szükséges műszaki, honosítási és regisztrációs ügyintézést, valamint a kapcsolódó dokumentációt.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
              {ADMIN_ITEMS.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-start gap-3 p-4 bg-black/30 border border-white/5 rounded-xl">
                    <div className="w-9 h-9 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0 text-[#D4AF37]">
                      <Icon className="w-[18px] h-[18px]" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-white mb-0.5">{item.title}</div>
                      <div className="text-xs text-white/50 leading-relaxed">{item.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 flex items-start gap-2.5 p-4 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/15 rounded-xl">
              <RefreshCw className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <p className="text-sm text-white/70 leading-relaxed">
                Önnek az átadás után már elsősorban az autó <span className="text-[#D4AF37] font-semibold">átírásával</span> kell foglalkoznia.
              </p>
            </div>
          </AccordionSection>

          {/* VIN */}
          {car.vin && (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6">
              <h2 className="font-semibold mb-2">Alvázszám (VIN)</h2>
              <p className="text-white/70 font-mono text-sm bg-white/5 p-3 rounded-lg">{car.vin}</p>
            </div>
          )}

          {/* Vehicle Origin */}
          {(() => {
            const vo = car.vehicle_origin || {};
            const hasData = Object.values(vo).some(v => v !== "" && v !== undefined && v !== null);
            if (!hasData) return null;
            const rows = [
              { label: "Helyszín", value: vo.location, icon: <MapPin className="w-3.5 h-3.5" /> },
              { label: "Távolság", value: vo.distance_km ? `${vo.distance_km} km` : null, icon: <Gauge className="w-3.5 h-3.5" /> },
              { label: "Forrás", value: vo.source_type, icon: <Users className="w-3.5 h-3.5" /> },
              { label: "Importált", value: vo.imported === true ? "Igen" : vo.imported === false ? "Nem" : null, icon: <Globe className="w-3.5 h-3.5" /> },
              { label: "Utolsó forgalomba helyezés országa", value: vo.last_registration_country, icon: <Globe className="w-3.5 h-3.5" /> },
              { label: "Adózás", value: vo.taxation_type, icon: <FileText className="w-3.5 h-3.5" /> },
            ];
            return (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6" data-testid="vehicle-origin-section">
                <h2 className="font-semibold mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  Jármű eredete
                </h2>
                <div className="space-y-0">
                  {rows.filter(r => r.value).map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
                      <span className="flex items-center gap-2 text-sm text-white/50">{r.icon}{r.label}</span>
                      <span className="text-sm text-white font-medium">{r.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Vehicle Inspection */}
          {hasVehicleInspection && (() => {
            const vi = vehicleInspectionData;
            const rows = [
              { label: "Műszaki érvényes", value: vi.technical_inspection_valid_until, icon: <ShieldCheck className="w-3.5 h-3.5" /> },
              { label: "Utolsó szerviz", value: vi.last_service_km ? `${vi.last_service_km} km` : null, icon: <Settings className="w-3.5 h-3.5" /> },
              { label: "Szervizkönyv", value: vi.service_book_status, icon: <BookOpen className="w-3.5 h-3.5" /> },
            ];
            return (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6" data-testid="vehicle-inspection-section">
                <h2 className="font-semibold mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  Járművizsgálat
                </h2>
                <div className="space-y-0">
                  {rows.filter(r => r.value).map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
                      <span className="flex items-center gap-2 text-sm text-white/50">{r.icon}{r.label}</span>
                      <span className="text-sm text-white font-medium">{r.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Vehicle History */}
          {(() => {
            const vh = car.vehicle_history || {};
            const hasData = Object.values(vh).some(v => v !== "" && v !== undefined && v !== null && v !== 0);
            if (!hasData) return null;
            const rows = [
              { label: "Első forgalomba helyezés", value: vh.first_registration, icon: <Calendar className="w-3.5 h-3.5" /> },
              { label: "Futásteljesítmény", value: vh.mileage ? `${vh.mileage} km` : null, icon: <Gauge className="w-3.5 h-3.5" /> },
              { label: "Tulajdonosok száma", value: vh.previous_owners, icon: <Users className="w-3.5 h-3.5" /> },
              { label: "Előélet", value: vh.unusual_usage, icon: <History className="w-3.5 h-3.5" /> },
              { label: "Kulcsok száma", value: vh.key_count, icon: <Key className="w-3.5 h-3.5" /> },
            ];
            return (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6" data-testid="vehicle-history-section">
                <h2 className="font-semibold mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                    <History className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  Járműtörténet
                </h2>
                <div className="space-y-0">
                  {rows.filter(r => r.value).map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
                      <span className="flex items-center gap-2 text-sm text-white/50">{r.icon}{r.label}</span>
                      <span className="text-sm text-white font-medium">{r.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Paint Thickness Diagram */}
          <PaintDiagram paintThickness={car.paint_thickness} />

          {/* Tire Inspection */}
          <TireInspection tireInspection={car.tire_inspection} />

          {/* Inspection Report */}
          {hasInspection && (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6" data-testid="inspection-section">
              <h2 className="font-semibold mb-4 sm:mb-6 flex items-center gap-2">
                <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                  <ClipboardCheck className="w-4 h-4 text-[#D4AF37]" />
                </div>
                Személyes ellenőrzés és teszt tapasztalatok
              </h2>

              {inspection.nincs_hiba && (
                <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <Check className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400 font-medium">Nincs észlelt hiba</span>
                </div>
              )}

              <div className="space-y-3">
                {INSPECTION_FIELDS.map(({ key, label }) => {
                  const value = inspection[key];
                  if (!value) return null;
                  return (
                    <div key={key} className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg">
                      <div className="w-6 h-6 mt-0.5 bg-[#D4AF37]/10 rounded flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-[#D4AF37]" />
                      </div>
                      <div>
                        <div className="text-xs text-white/40 mb-0.5">{label}</div>
                        <div className="text-sm text-white/80">{value}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Motor és egyéb */}
          {motorImages.length > 0 && (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6" data-testid="motor-images-section">
              <h2 className="font-semibold mb-4 sm:mb-6 flex items-center gap-2">
                <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                  <Wrench className="w-4 h-4 text-[#D4AF37]" />
                </div>
                Motor és egyéb
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {motorImages.map((item, i) => (
                  <div key={i} className="relative group" data-testid={`motor-item-${i}`}>
                    {item.type === "video" ? (
                      <div className="aspect-square rounded-lg overflow-hidden bg-white/5 cursor-pointer relative"
                        onClick={() => window.open(getImageUrl(item.url), '_blank')}>
                        <img src={cldVideoPoster(item.url) || getImageUrl(item.url)} alt={item.description || ""} className="w-full h-full object-cover" loading="lazy" onError={(e)=>{e.currentTarget.style.visibility='hidden';}} />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                            <Play className="w-5 h-5 text-white fill-white" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-square rounded-lg overflow-hidden bg-white/5 cursor-pointer"
                        onClick={() => {
                          const imgs = motorImages.filter(m => m.type !== "video").map(m => m.url);
                          const idx = motorImages.slice(0, i).filter(m => m.type !== "video").length;
                          openLightbox(imgs, idx);
                        }}>
                        <img src={cldThumb(item.url, 400)} alt={item.description || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" onError={(e)=>{const el=e.currentTarget;const t=Number(el.dataset.t||0);if(t>=1){el.style.visibility='hidden';return;}el.dataset.t=t+1;el.src=getImageUrl(item.url);}} />
                      </div>
                    )}
                    {item.description && (
                      <p className="text-[10px] text-white/40 mt-1 truncate">{item.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Damages */}
          {damages.length > 0 && (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6" data-testid="damages-section">
              <h2 className="font-semibold mb-4 sm:mb-6 flex items-center gap-2">
                <div className="w-8 h-8 bg-yellow-500/10 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                </div>
                Állapot és hibák
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {damages.map((d, i) => (
                  <div key={i} className="rounded-xl overflow-hidden bg-white/[0.03] border border-white/5" data-testid={`damage-item-${i}`}>
                    {d.image && (
                      <div className="aspect-[16/10] cursor-pointer" onClick={() => {
                        const damageImages = damages.filter(dm => dm.image).map(dm => dm.image);
                        const idx = damages.slice(0, i).filter(dm => dm.image).length;
                        openLightbox(damageImages, idx);
                      }}>
                        <img src={cldThumb(d.image, 700)} alt={d.description || ""} className="w-full h-full object-cover" loading="lazy" onError={(e)=>{const el=e.currentTarget;const t=Number(el.dataset.t||0);if(t>=1){el.style.visibility='hidden';return;}el.dataset.t=t+1;el.src=getMediumUrl(d.image);}} />
                      </div>
                    )}
                    <div className="p-3">
                      <p className="text-sm text-white/70">{d.description || "Nincs leírás"}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          {documents.length > 0 && (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6" data-testid="documents-section">
              <h2 className="font-semibold mb-4 sm:mb-6 flex items-center gap-2">
                <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-[#D4AF37]" />
                </div>
                Dokumentumok
              </h2>
              <div className="space-y-2">
                {documents.map((doc, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-white/[0.03] border border-white/5 rounded-lg" data-testid={`document-item-${i}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{doc.title || "Dokumentum"}</p>
                        {doc.description && <p className="text-xs text-white/40 truncate">{doc.description}</p>}
                      </div>
                    </div>
                    <a
                      href={getImageUrl(doc.file_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded-lg text-xs font-medium hover:bg-[#D4AF37]/20 flex-shrink-0 ml-3"
                      data-testid={`document-view-${i}`}
                    >
                      <Download className="w-3.5 h-3.5" /> Megnyitás
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Equipment */}
          {car.equipment && (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6" data-testid="equipment-section">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                  <Settings className="w-4 h-4 text-[#D4AF37]" />
                </div>
                Felszereltség
              </h2>
              <div className="grid sm:grid-cols-2 gap-2">
                {car.equipment.split(',').map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-white/70">{item.trim()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Personal viewing / test drive note */}
          <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 flex flex-col sm:flex-row items-start gap-4" data-testid="viewing-note">
            <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0 text-[#D4AF37]">
              <Eye className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-sm mb-1">Személyes megtekintés</h3>
              <p className="text-sm text-white/55 leading-relaxed">
                A jármű előre egyeztetett időpontban személyesen is megtekinthető. Próbavezetés az adott járműtől függően egyeztethető.
              </p>
            </div>
          </div>

          {/* Contact section */}
          <div id="kapcsolat" className="bg-gradient-to-br from-[#0A0A0A] to-[#0d0b06] border border-[#D4AF37]/20 rounded-xl sm:rounded-2xl p-5 sm:p-7 scroll-mt-24" data-testid="contact-section">
            <h2 className="font-display font-bold text-lg sm:text-xl mb-1">Érdekli ez az autó?</h2>
            <p className="text-sm text-white/55 mb-5">Vegye fel velünk a kapcsolatot, és segítünk a részletekben.</p>

            {isSold ? (
              <div className="p-4 bg-white/5 border border-white/10 rounded-xl text-center text-sm text-white/60" data-testid="contact-sold-note">
                Ez az autó már <span className="font-bold text-white/80">eladva</span>. Nézze meg további elérhető járműveinket.
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-3 mb-5">
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-3.5 bg-[#25D366]/15 border border-[#25D366]/30 text-[#25D366] font-semibold rounded-xl hover:bg-[#25D366]/25 text-sm transition-colors" data-testid="contact-whatsapp-btn">
                    WhatsApp üzenet
                  </a>
                  <a href={emailHref}
                    className="flex items-center justify-center gap-2 py-3.5 bg-white/5 border border-white/10 text-white font-semibold rounded-xl hover:bg-white/10 text-sm transition-colors" data-testid="contact-email-btn">
                    {contactInfo.email}
                  </a>
                </div>

                {contactSubmitted ? (
                  <div className="text-center py-8" data-testid="contact-success">
                    <div className="w-14 h-14 mx-auto mb-3 bg-green-500/15 rounded-full flex items-center justify-center">
                      <Check className="w-7 h-7 text-green-500" />
                    </div>
                    <h3 className="font-semibold mb-1">Köszönjük!</h3>
                    <p className="text-white/55 text-sm">Hamarosan felvesszük Önnel a kapcsolatot.</p>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-3" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input type="text" required placeholder="Név *" value={contact.name}
                        onChange={(e) => setContact({ ...contact, name: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                        data-testid="contact-name-input" />
                      <input type="tel" required placeholder="Telefon *" value={contact.phone}
                        onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                        data-testid="contact-phone-input" />
                    </div>
                    <textarea rows={3} placeholder="Üzenet (opcionális)" value={contact.message}
                      onChange={(e) => setContact({ ...contact, message: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37]/50 focus:outline-none resize-none"
                      data-testid="contact-message-input" />
                    <Button type="submit" disabled={contactSubmitting}
                      className="w-full btn-gold rounded-xl font-bold py-5 text-sm inline-flex items-center justify-center gap-2"
                      data-testid="contact-submit-btn">
                      {contactSubmitting ? "Küldés..." : <>Érdeklődés küldése <Send className="w-4 h-4" /></>}
                    </Button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* WhatsApp Group Float */}
      <a href={waGroupUrl} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-4 z-40 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-2xl lg:hidden" data-testid="floating-wa-group">
        <MessageCircle className="w-7 h-7 text-white" />
      </a>

      {/* Lightbox */}
      <Lightbox images={lightboxImages} activeIndex={lightboxIndex} isOpen={lightboxOpen} onClose={() => setLightboxOpen(false)} getImageUrl={getImageUrl} />
    </div>
  );
}
